import { useState } from "react";
import { MailWarning } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";

/**
 * VerificationPendingBanner — persistent top-of-page banner for signed-in
 * users whose email is not yet verified.
 *
 * Rendered globally by SiteShell so it appears on every authenticated page.
 * Dismisses to `hidden` when `user.email_verified === true`.
 */
export function VerificationPendingBanner() {
  const { user } = useAuth();
  const [sending, setSending] = useState(false);

  // Hide for unauthenticated users, admins (grand-fathered), and anyone
  // already verified. AuthContext exposes `email_verified` from /auth/me.
  if (!user) return null;
  if (user.email_verified) return null;

  const handleResend = async () => {
    setSending(true);
    try {
      await api.post("/auth/verify-email/resend", {});
      toast.success("Verification email sent — check your inbox in a moment.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "cooldown") {
        toast.info(detail.message || `Please wait ${detail.retry_after_seconds || 60}s before trying again.`);
      } else {
        const msg = detail && typeof detail === "object" ? detail.message : (typeof detail === "string" ? detail : "Could not resend right now.");
        toast.error(msg);
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="w-full border-b border-[hsl(var(--sb-clay))]/30 bg-[hsl(var(--sb-clay))]/10"
      role="status"
      data-testid="verification-pending-banner"
    >
      <div className="mx-auto max-w-6xl px-4 py-2.5 flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          <MailWarning size={16} className="mt-0.5 shrink-0 text-[hsl(var(--sb-clay))]" />
          <p className="text-xs sm:text-sm text-foreground leading-snug">
            <span className="font-semibold">Please verify your email</span> to unlock messaging, applications, and Coffee Chats. Check your inbox for the link we sent to <span className="font-mono">{user.email}</span>.
          </p>
        </div>
        <button
          type="button"
          onClick={handleResend}
          disabled={sending}
          className="text-xs sm:text-sm font-semibold underline-offset-4 hover:underline text-[hsl(var(--sb-clay))] disabled:opacity-60 whitespace-nowrap"
          data-testid="verification-pending-resend-btn"
        >
          {sending ? "Sending…" : "Resend verification email"}
        </button>
      </div>
    </div>
  );
}

export default VerificationPendingBanner;
